import React, { FC, Dispatch, SetStateAction } from "react";
import {
  Paper,
  Typography,
  Table,
  TableRow,
  TableBody,
  TableContainer,
  TablePagination,
  Backdrop,
  CircularProgress,
  SelectChangeEvent,
} from "@mui/material";
import SearchBar from "../SearchBar/SearchBar";
import EnhancedTableHead from "../TableHead/EnhancedTableHead";
import TableToolbar from "../Toolbar/TableToolbar";
import TableToolbarProd from "../Toolbar/TableToolbarProd";
import TableToolbarDealer from "../Toolbar/TableToolbarDealer";
import ConfirmationModal from "../Modals/ConfirmationModal";
import SalesOrderWarningModal from "../Modals/SalesOrderWarningModal";
import AdminOrderModal from "../Modals/AdminOrderModal";
import DuplicateQuoteModal from "../Modals/DuplicateQuoteModal";
import ArchiveModal from "../Modals/ArchiveModal";
import RoleTableCells from "../RoleTableCells";
import SaberisModal from "../Modals/SaberisModal";
import { styled } from "@mui/material/styles";
import { QuoteObj } from "../../../../GlobalTypes";
import { TableState } from "../../../../pages/DealerZone/DealerZone";
import useQuotesTableSelector, {
  AdminQuoteObj,
} from "./useQuotesTableSelector";

export interface QuotesTableProps {
  searchTerm: string;
  filterBy: string;
  showLeadsOnly?: boolean;
  isSearch: boolean;
  setIsSearch: Dispatch<SetStateAction<boolean>>;
  totalLength: number;
  page: number;
  rowsPerPage: number;
  userId: string;
  showTab: string;
  data:
    | string
    | {
        currentPage: AdminQuoteObj[] | QuoteObj[];
        totalResults?: number;
        missingInCommaList?: string[];
      };
  isLoading: boolean;
  error: unknown;
  isFetching: boolean;
  setState: Dispatch<SetStateAction<TableState>>;
  setReSearch: Dispatch<SetStateAction<boolean>>;
  handleArchive?: (e: SelectChangeEvent<string>) => void;
  showArchive?: string;
}

const StyledRootDiv = styled("div")({
  width: "100%",
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(2),
  position: "relative",
}));

const StyledTypography = styled(Typography)({
  display: "flex",
  justifyContent: "flex-start",
  padding: 20,
  fontWeight: "bold",
});

const StyledBackdrop = styled(Backdrop)({
  position: "absolute",
  width: "100%",
  height: "50vh",
  zIndex: 10,
});

const QuotesTable: FC<QuotesTableProps> = ({
  searchTerm,
  filterBy,
  showLeadsOnly,
  isSearch,
  setIsSearch,
  totalLength,
  page,
  rowsPerPage,
  userId,
  showTab,
  data,
  isLoading,
  error,
  isFetching,
  setState,
  setReSearch,
  handleArchive,
  showArchive,
}) => {
  const {
    handleChange,
    handleFilterByChange,
    onKeyDown,
    handleReset,
    orderAckErr,
    handleCheckOrderAck,
    handleAdminChange,
    openAdminModal,
    warnModal,
    handleWarnModalClose,
    handleAdminOpen,
    handleAdminClose,
    mutation,
    archiveMutation,
    handleViewClick,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheck,
    unCheckAll,
    isSelected,
    loading,
    setLoading,
    loadDuplicate,
    openDuplicateModal,
    closeDuplicateModal,
    openModal,
    loadingModal,
    archiveModal,
    handleOpenModal,
    openSaberis,
    handleCloseSaberis,
    handleOpenSaberis,
    handleOpenModalArchive,
    duplicateQuote,
    hoverId,
    handleChangeArchive,
    handleChangeTextBoxArchive,
    mouseEnter,
    mouseLeave,
    adminModalObj,
    filterSearch,
    selectedQuotes,
    role,
    adminOrderNums,
    soMatch,
    sendOrderAck,
    handleCloseModalArchive,
    archiveReason,
    archiveReasonText,
    duplicate,
    handleCloseModal,
    rows,
    handleShowLeadsOnly,
  } = useQuotesTableSelector({
    searchTerm,
    isSearch,
    setIsSearch,
    userId,
    showTab,
    data,
    isFetching,
    setState,
    setReSearch,
  });

  if (error)
    return <div>An error has occured, please try refreshing the page.</div>;

  return (
    <>
      <SearchBar
        filterBy={filterBy}
        handleFilterByChange={handleFilterByChange}
        handleReset={handleReset}
        onKeyDown={onKeyDown}
        filterSearch={filterSearch}
        handleChange={handleChange}
        showTab={showTab}
        searchTerm={searchTerm}
        showArchive={showArchive}
        handleArchive={handleArchive}
        showLeadsOnly={showLeadsOnly}
        handleShowLeadsOnly={handleShowLeadsOnly}
      />

      {!isFetching && data === "No Quotes Found" && isSearch ? (
        <StyledTypography>
          No {showTab === "Insights" ? "Quotes" : showTab} Found for your Search
        </StyledTypography>
      ) : !isFetching && data === "No Quotes Found" && !isSearch ? (
        <StyledTypography>
          No {showTab === "Insights" ? "Quotes" : showTab} Found
        </StyledTypography>
      ) : (
        <StyledRootDiv>
          {(role === "Admin" || role === "Inside Sales") &&
            showTab !== "Insights" && (
              <>
                <AdminOrderModal
                  selectedQuote={selectedQuotes[0] as AdminQuoteObj}
                  open={openAdminModal}
                  handleClose={handleAdminClose}
                  modalText={adminModalObj.modalText}
                  mutation={adminModalObj.mutation}
                  name={adminModalObj.name}
                  value={adminOrderNums}
                  soMatch={soMatch}
                  handleChange={handleAdminChange}
                  sendOrderAck={sendOrderAck}
                  handleCheckOrderAck={handleCheckOrderAck}
                  orderAckErr={orderAckErr}
                />

                <SalesOrderWarningModal
                  open={warnModal}
                  handleClose={handleWarnModalClose}
                  nextModal={handleAdminOpen}
                />
              </>
            )}

          <ArchiveModal
            handleClose={handleCloseModalArchive}
            mutation={archiveMutation}
            open={archiveModal}
            archiveReason={archiveReason}
            handleChange={handleChangeArchive}
            status={selectedQuotes[0]?.status}
            archiveReasonTextBox={archiveReasonText}
            handleChangeTextBox={handleChangeTextBoxArchive}
          />

          <DuplicateQuoteModal
            handleClose={closeDuplicateModal}
            open={duplicate.open}
            loading={loadDuplicate}
            quoteNumber={duplicate.quoteNumber}
            error={duplicate.error}
            duplicateQuote={duplicateQuote}
          />

          <ConfirmationModal
            mutation={mutation}
            loading={loadingModal}
            showTab={showTab}
            handleClose={handleCloseModal}
            open={openModal}
          />

          <SaberisModal
            handleClose={handleCloseSaberis}
            open={openSaberis}
            quoteId={selectedQuotes.length === 1 ? selectedQuotes[0]._id : ""}
          />

          {typeof data === "object" &&
          data.missingInCommaList?.length &&
          !isFetching &&
          !isLoading ? (
            <StyledTypography>
              No results found for:{" "}
              {data.missingInCommaList.map((itm, idx) => (
                <span style={{ paddingLeft: 5 }} key={itm}>
                  {data?.missingInCommaList?.length &&
                  idx === data?.missingInCommaList?.length - 1
                    ? itm
                    : `${itm},`}
                </span>
              ))}
            </StyledTypography>
          ) : null}

          <StyledPaper>
            {showTab === "Insights" ? null : role === "Production" ? (
              <TableToolbarProd
                quote={selectedQuotes[0] as AdminQuoteObj}
                numSelected={selectedQuotes.length}
                unCheckAll={unCheckAll}
              />
            ) : role === "Admin" || role === "Inside Sales" ? (
              <TableToolbar
                handleOpenModal={handleOpenModal}
                showTab={showTab}
                quote={selectedQuotes as AdminQuoteObj[]}
                numSelected={selectedQuotes.length}
                handleOpen={handleAdminOpen}
                unCheckAll={unCheckAll}
                searchTerm={searchTerm}
                searchTermFilter={filterBy}
                isSearch={isSearch}
                openDuplicateModal={openDuplicateModal}
                handleOpenModalArchive={handleOpenModalArchive}
                showArchive={showArchive}
              />
            ) : (
              <TableToolbarDealer
                showTab={showTab}
                numSelected={selectedQuotes.length}
                unCheckAll={unCheckAll}
                searchTerm={searchTerm}
                searchTermFilter={filterBy}
                isSearch={isSearch}
                handleOpenModalArchive={handleOpenModalArchive}
                status={selectedQuotes[0]?.status}
                showArchive={showArchive}
                openDuplicateModal={openDuplicateModal}
                openSaberisModal={handleOpenSaberis}
              />
            )}

            {isFetching || isLoading ? (
              <StyledBackdrop open={isFetching || isLoading}>
                <CircularProgress color="primary" />
              </StyledBackdrop>
            ) : (
              <>
                <TableContainer>
                  <Table
                    style={{ minWidth: 750 }}
                    aria-labelledby="tableTitle"
                    size={"small"}
                    aria-label="enhanced table"
                  >
                    <EnhancedTableHead
                      showTab={showTab}
                      displayLeadNumberColumn={
                        showLeadsOnly ||
                        (filterBy === "leadNumber" &&
                          searchTerm !== "" &&
                          searchTerm !== undefined &&
                          searchTerm !== null)
                      }
                    />

                    <TableBody>
                      {rows.length
                        ? rows.map((row) => {
                            const isItemSelected = isSelected(row._id);
                            return (
                              <TableRow
                                hover
                                role="checkbox"
                                tabIndex={-1}
                                key={row._id}
                                onMouseEnter={() => mouseEnter(row._id)}
                                onMouseLeave={mouseLeave}
                              >
                                <RoleTableCells
                                  row={row}
                                  tab={showTab}
                                  role={role}
                                  handleViewClick={handleViewClick}
                                  loading={loading}
                                  isItemSelected={isItemSelected}
                                  handleCheck={handleCheck}
                                  numSelected={selectedQuotes.length}
                                  hoverId={hoverId}
                                  setLoading={setLoading}
                                  displayLeadNumberCells={
                                    showLeadsOnly ||
                                    (filterBy === "leadNumber" &&
                                      searchTerm !== "" &&
                                      searchTerm !== undefined &&
                                      searchTerm !== null)
                                  }
                                />
                              </TableRow>
                            );
                          })
                        : null}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[15, 20, 25]}
                  component="div"
                  count={totalLength}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </StyledPaper>
        </StyledRootDiv>
      )}
    </>
  );
};

export default QuotesTable;
